import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack } from '@mui/material';

import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import GlobalDatagrid from '../../components/mui/DataGrid/GlobalDatagrid';
import GlobalDeleteDialog from '../../components/mui/Dialogue/GlobalDeleteDialog';

import { SkillxAssignments_Table_Head } from '../../components/datagrid/skillx/SkillxAssignmentsTableHead';
import {
  getSkillxAssignments,
  deleteSkillxAssignment,
} from '../../redux/features/skillxSlice';
import AddSkillxAssignment from './AddSkillxAssignment';
import UpdateSkillxAssignment from './UpdateSkillxAssignment';

export default function SkillxAssignments() {
  const dispatch = useDispatch();
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const { assignments, loading } = useSelector((state) => state.skillx);
  const { gridSize, pageNum, search } = paginationData;

  // The query params reused everywhere: initial load, each pagination/search
  // change, and the refresh after a create/update/delete.
  const data = useMemo(
    () => ({ page: pageNum, limit: gridSize, search: search }),
    [pageNum, gridSize, search],
  );

  useEffect(() => {
    if (pageNum) dispatch(getSkillxAssignments(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, gridSize, search]);

  // Edit and delete live in the row rather than behind the anchor drawer, so
  // the controls sit beside the assignment they act on.
  const columns = useMemo(
    () => [
      ...SkillxAssignments_Table_Head,
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 300,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <UpdateSkillxAssignment rowData={params.row} dispatchparams={data} />
            <GlobalDeleteDialog
              title="Delete Assignment"
              deleteNote={`Are you sure you want to delete "${params.row?.title}"?`}
              api={deleteSkillxAssignment(params.row?.id)}
              upDateCall={getSkillxAssignments(data)}
              loading={loading}
            />
          </Stack>
        ),
      },
    ],
    [data, loading],
  );

  return (
    <GlobalWrapper
      title="SkillX Assignments"
      actions={<AddSkillxAssignment dispatchparams={data} />}
    >
      <Box sx={{ width: '100%', padding: 0 }}>
        <GlobalDatagrid
          tableHead={columns}
          rows={assignments?.data || []}
          meta={assignments?.meta?.totalRecords || 0}
          loading={loading}
          rowUniqueId="id"
          rowClick={false}
        />
      </Box>
    </GlobalWrapper>
  );
}
